import { Inject, Injectable } from '@angular/core';
import { HttpClient, HttpEvent, HttpResponse } from '@angular/common/http';
import { SHARED_ENVIRONMENT } from '@cp/shared/environment/environment.token';
import { Environment } from '@cp/shared/environment/environment.interface';
import { BehaviorSubject, Observable, switchMap } from 'rxjs';
import { SharedHttpService } from '../../shared/shared-http.service';
import { AddressHttpSectionValue } from '../profile/profile-http.models';
import { ResourcesHttpPaths } from './resources-http.paths';
import {
  ComplainIncidentHttpModel,
  LastWorkedShiftHttpModel,
  NmcNmbiRecordHttpModel,
  NmcNmbiRenewalSchemeHttpModel,
} from './resources-http.models';

@Injectable({
  providedIn: 'root',
})
export class ResourcesHttpService extends SharedHttpService {
  private _refreshIsClaimMade = new BehaviorSubject<void>(undefined);

  public readonly pathSplitChar = '\\';
  public policiesProcedures$ = this._http.get<Array<string>>(
    this._environment.apiEndpoint + ResourcesHttpPaths.policiesAndProcedures,
  );
  public nmcStatus$ = this._http.get<NmcNmbiRecordHttpModel>(
    this._environment.apiEndpoint + ResourcesHttpPaths.nmcNmbiStatus,
    { params: { scheme: 'NMC' } },
  );
  public nmbiStatus$ = this._http.get<NmcNmbiRecordHttpModel>(
    this._environment.apiEndpoint + ResourcesHttpPaths.nmcNmbiStatus,
    { params: { scheme: 'NMBI' } },
  );
  public primaryAddress$ = this._http.get<AddressHttpSectionValue>(
    this._environment.apiEndpoint + ResourcesHttpPaths.primaryAddress,
  );
  public lastWorkedShifts$ = this._http.get<Array<LastWorkedShiftHttpModel>>(
    this._environment.apiEndpoint + ResourcesHttpPaths.lastWorkedShifts,
  );
  public isClaimMade$ = this._refreshIsClaimMade.pipe(
    switchMap(() =>
      this._http.get<boolean>(
        this._environment.apiEndpoint + ResourcesHttpPaths.isClaimMade,
      ),
    ),
  );

  constructor(
    private _http: HttpClient,
    @Inject(SHARED_ENVIRONMENT)
    private _environment: Environment,
  ) {
    super();
  }

  public reloadIsClaimMade(): void {
    this._refreshIsClaimMade.next();
  }

  public submitNmcNmbiQuery(
    query: string | null,
    scheme: NmcNmbiRenewalSchemeHttpModel,
  ): Observable<HttpResponse<object>> {
    return this._http.put(
      this._environment.apiEndpoint + ResourcesHttpPaths.nmcNmbiSubmitQuery,
      { text: query },
      {
        params: {
          scheme,
        },
        observe: 'response',
      },
    );
  }

  public downloadPolicyAndProcedure(path: string): Observable<Blob> {
    return this._http.post(
      this._environment.apiEndpoint +
        ResourcesHttpPaths.downloadPolicyAndProcedure,
      { path },
      {
        responseType: 'blob',
      },
    );
  }

  public claimNmcNmbiFee(
    scheme: NmcNmbiRenewalSchemeHttpModel,
  ): Observable<HttpResponse<object>> {
    return this._http.put(
      this._environment.apiEndpoint + ResourcesHttpPaths.nmcNmbiClaimFee,
      undefined,
      {
        params: {
          scheme,
        },
        observe: 'response',
      },
    );
  }

  public submitComplaintsIncidents(
    complaintsIncidents: ComplainIncidentHttpModel,
  ): Observable<HttpResponse<void>> {
    return this._http.post<void>(
      this._environment.apiEndpoint + ResourcesHttpPaths.submitComplaints,
      complaintsIncidents,
      {
        observe: 'response',
      },
    );
  }

  public submitClaimUniformReimbursement(
    formData: FormData,
  ): Observable<HttpEvent<object>> {
    return this._http.post(
      this._environment.apiEndpoint +
        ResourcesHttpPaths.submitClaimUniformReimbursement,
      formData,
      { reportProgress: true, observe: 'events' },
    );
  }
}
